import { useEffect } from 'react';

interface SEOProps {
  title: string;
  description: string;
  keywords: string;
  canonical: string;
  ogType?: string;
  schema?: object;
}

export function useSEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  schema,
}: SEOProps) {
  useEffect(() => {
    // Update title
    document.title = title;

    // Update or create meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    // Update basic meta tags
    updateMetaTag('description', description);
    updateMetaTag('keywords', keywords);

    // Update Open Graph tags
    updateMetaTag('og:title', title, true);
    updateMetaTag('og:description', description, true);
    updateMetaTag('og:type', ogType, true);
    updateMetaTag('og:url', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${canonical}`, true);

    // Update Twitter Card tags
    updateMetaTag('twitter:title', title);
    updateMetaTag('twitter:description', description);

    // Update canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${canonical}`);

    // Update last-modified
    const lastModified = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', lastModified);

    // Add or update Schema.org JSON-LD
    if (schema) {
      let scriptTag = document.querySelector('script[type="application/ld+json"]');
      
      if (!scriptTag) {
        scriptTag = document.createElement('script');
        scriptTag.setAttribute('type', 'application/ld+json');
        document.head.appendChild(scriptTag);
      }
      
      scriptTag.textContent = JSON.stringify(schema);
    }

    // Cleanup function
    return () => {
      // Remove schema script when component unmounts
      if (schema) {
        const scriptTag = document.querySelector('script[type="application/ld+json"]');
        if (scriptTag) {
          scriptTag.remove();
        }
      }
    };
  }, [title, description, keywords, canonical, ogType, schema]);
}

// Schema.org generators
export const generateLocalBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/#business`,
  name: 'U.B.K Window Tint Services',
  image: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/og-image.jpg`,
  url: import.meta.env.VITE_SITE_URL || 'https://example.com',
  telephone: '+14438000348',
  email: 'ubkwindowtintservices@gmail.com',
  address: {
    '@type': 'PostalAddress',
    streetAddress: '313 Nottingham Road',
    addressLocality: 'Baltimore',
    addressRegion: 'MD',
    postalCode: '21229',
    addressCountry: 'US',
  },
  geo: {
    '@type': 'GeoCoordinates',
    latitude: 39.2845,
    longitude: -76.6544,
  },
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '08:00',
      closes: '17:00',
    },
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: 'Sunday',
      opens: '08:00',
      closes: '12:00',
    },
  ],
  priceRange: '$$',
  areaServed: {
    '@type': 'City',
    name: 'Baltimore',
  },
});

export const generateServiceSchema = (serviceName: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'Service',
  serviceType: serviceName,
  description,
  provider: {
    '@type': 'LocalBusiness',
    name: 'U.B.K Window Tint Services',
    telephone: '+14438000348',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '313 Nottingham Road',
      addressLocality: 'Baltimore',
      addressRegion: 'MD',
      postalCode: '21229',
      addressCountry: 'US',
    },
  },
  areaServed: {
    '@type': 'City',
    name: 'Baltimore',
  },
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${url}`,
});

export const generateFAQSchema = (faqs: Array<{ question: string; answer: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((faq) => ({
    '@type': 'Question',
    name: faq.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: faq.answer,
    },
  })),
});

export const generateWebPageSchema = (name: string, description: string, url: string) => ({
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  name,
  description,
  url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${url}`,
  publisher: {
    '@type': 'Organization',
    name: 'U.B.K Window Tint Services',
    logo: {
      '@type': 'ImageObject',
      url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/logo.png`,
    },
  },
});

export const generateBreadcrumbSchema = (items: Array<{ name: string; url: string }>) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}${item.url}`,
  })),
});
